import { Pencil, Trash2, Copy } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConditionBadge } from "@/components/ConditionBadge";
import { formatPrice } from "@/lib/formatPrice";
import StickerPrintControls from "@/components/admin/StickerPrintControls";

interface Product {
  id: number;
  name: string;
  sku: string | null;
  price: number;
  sale_price: number | null;
  condition: number;
  is_new: number;
  quantity: number;
  category_id: number | null;
  categoryName: string | null;
  size: string | null;
  bin_location: string | null;
  image: string | null;
}

interface ProductsTableProps {
  products: Product[];
  selected: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  onDuplicate: (product: Product) => void;
  onDelete: (id: number, name: string) => void;
  onPrintSku: (product: Product, quantity: number) => void;
  printingProductId: number | null;
  isDeletePending: boolean;
}

export default function ProductsTable({
  products,
  selected,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  someSelected,
  onDuplicate,
  onDelete,
  onPrintSku,
  printingProductId,
  isDeletePending,
}: ProductsTableProps) {
  const [printQuantities, setPrintQuantities] = useState<Record<number, number>>({});

  function getPrintQuantity(productId: number): number {
    return printQuantities[productId] ?? 1;
  }

  function setPrintQuantity(productId: number, quantity: number) {
    setPrintQuantities((prev) => ({ ...prev, [productId]: quantity }));
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {products.map((product) => (
          <div key={product.id} className="bg-white rounded-lg border border-border/60 p-3 flex flex-col gap-3">
            <div className="flex gap-3">
            {product.image ? (
              <img
                src={`/uploads/${product.id}-thumb.webp`}
                alt={product.name}
                loading="lazy"
                decoding="async"
                className="size-16 rounded-lg object-cover shrink-0 bg-muted"
              />
            ) : (
              <div className="size-16 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs shrink-0">
                N/A
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{product.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{product.sku ?? "—"} · {product.categoryName ?? "—"}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-sm font-semibold">{formatPrice(product.price)} KM</span>
                {product.sale_price != null && (
                  <span className="text-xs text-red-500">{formatPrice(product.sale_price)} KM</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                {product.is_new ? (
                  <span className="text-[10px] font-semibold bg-foreground text-white px-2 py-0.5 rounded-full">Novo</span>
                ) : (
                  <ConditionBadge value={product.condition} />
                )}
                {product.size && (
                  <span className="text-[10px] font-medium text-foreground/70">{product.size}</span>
                )}
                <span className={`text-[10px] ${product.quantity === 0 ? "text-red-500 font-medium" : "text-muted-foreground"}`}>
                  Qty: {product.quantity}
                </span>
                {product.bin_location && (
                  <span className="text-[10px] text-muted-foreground">{product.bin_location}</span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <Button variant="ghost" size="icon-sm" asChild>
                <Link to="/admin/products/$id/edit" params={{ id: String(product.id) }}>
                  <Pencil className="size-3.5" />
                </Link>
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => onDuplicate(product)}>
                <Copy className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-destructive"
                onClick={() => onDelete(product.id, product.name)}
                disabled={isDeletePending}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
            </div>
            {product.sku && (
              <StickerPrintControls
                compact
                quantity={getPrintQuantity(product.id)}
                onQuantityChange={(q) => setPrintQuantity(product.id, q)}
                onPrint={() => onPrintSku(product, getPrintQuantity(product.id))}
                canPrint
                isPrinting={printingProductId === product.id}
              />
            )}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-lg border border-border/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-3 py-3">
                <Checkbox
                  checked={
                    allSelected
                      ? true
                      : someSelected && !allSelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={onToggleSelectAll}
                  className="cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Slika</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Naziv</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">SKU</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Cijena</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Akcijska</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Veličina</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Stanje oštećenja</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Količina</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Kategorija</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Lokacija</th>
              <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Akcije</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {products.map((product) => (
              <tr
                key={product.id}
                className={`hover:bg-muted/20 transition-colors ${selected.has(product.id) ? "bg-blue-50/50" : ""}`}
              >
                <td className="px-3 py-3">
                  <Checkbox
                    checked={selected.has(product.id)}
                    onCheckedChange={() => onToggleSelect(product.id)}
                    className="cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3">
                  {product.image ? (
                    <img
                      src={`/uploads/${product.id}-thumb.webp`}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="size-10 rounded object-cover"
                    />
                  ) : (
                    <div className="size-10 rounded bg-muted flex items-center justify-center text-muted-foreground text-xs">
                      N/A
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-medium">{product.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{product.sku ?? "—"}</td>
                <td className="px-4 py-3">{formatPrice(product.price)} KM</td>
                <td className="px-4 py-3">
                  {product.sale_price != null ? `${formatPrice(product.sale_price)} KM` : "—"}
                </td>
                <td className="px-4 py-3">{product.size ?? "—"}</td>
                <td className="px-4 py-3">
                  {product.is_new ? (
                    <span className="text-[10px] font-semibold bg-foreground text-white px-2 py-0.5 rounded-full">Novo</span>
                  ) : (
                    <ConditionBadge value={product.condition} />
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={product.quantity === 0 ? "text-red-500 font-medium" : ""}>
                    {product.quantity}
                  </span>
                </td>
                <td className="px-4 py-3">{product.categoryName ?? "—"}</td>
                <td className="px-4 py-3">{product.bin_location ?? "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon-sm" asChild>
                      <Link to="/admin/products/$id/edit" params={{ id: String(product.id) }}>
                        <Pencil className="size-4" />
                      </Link>
                    </Button>
                    <StickerPrintControls
                      compact
                      quantity={getPrintQuantity(product.id)}
                      onQuantityChange={(q) => setPrintQuantity(product.id, q)}
                      onPrint={() => onPrintSku(product, getPrintQuantity(product.id))}
                      canPrint={!!product.sku}
                      isPrinting={printingProductId === product.id}
                    />
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDelete(product.id, product.name)}
                      disabled={isDeletePending}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
